import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useUpdateQuestion } from '@/api/useAdminPastPaper';
import { Database } from '@/_constants/database.types';

type QuestionRow = Database['public']['Tables']['questions']['Row'];

interface PageNumbersEditorProps {
  question: QuestionRow;
  pastPaperId: string;
  field: 'image_page_numbers' | 'reading_insert_page_numbers';
  label?: string;
}

interface FormValues {
  pages: string;
}

const PageNumbersEditor: FC<PageNumbersEditorProps> = ({ question, pastPaperId, field, label }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { mutate: updateQuestion, isPending } = useUpdateQuestion();

  const currentPages = question[field] as number[] | null;
  const displayValue = currentPages && currentPages.length > 0 ? `p.${currentPages.join(', ')}` : '--';

  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      pages: currentPages ? currentPages.join(', ') : '',
    },
  });

  const handleStartEdit = () => {
    reset({ pages: currentPages ? currentPages.join(', ') : '' });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = (data: FormValues) => {
    const trimmed = data.pages.trim();
    let pages: number[] | null = null;

    if (trimmed) {
      const parsed = trimmed
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n > 0);

      if (parsed.length > 0) {
        pages = parsed;
      }
    }

    updateQuestion(
      {
        questionId: question.id,
        pastPaperId,
        updates: { [field]: pages },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="flex items-center gap-1">
        <input
          {...register('pages')}
          type="text"
          placeholder="e.g. 2, 3"
          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={isPending}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <button
          type="submit"
          disabled={isPending}
          className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
          title="Save"
        >
          <CheckIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={isPending}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          title="Cancel"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </form>
    );
  }

  return (
    <div className="flex items-center gap-1 group">
      {label && <span className="text-xs text-gray-500 mr-1">{label}:</span>}
      <span className="text-gray-600 text-sm">{displayValue}</span>
      <button
        onClick={handleStartEdit}
        className="p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Edit"
      >
        <PencilIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default PageNumbersEditor;
